import SubscriptionCard from "@/components/Bundle/SubcriptionCard";
import { useMetaInfos } from "@/hooks/useMeta";
import { useSubscriptions } from "@/hooks/useSubscription";
import useTranslation from "@/hooks/useTranslation";
import { GetSubsDtoField } from "@/interfaces/bundle.interface";
import CommonLayoutWithAuth from "@/layouts/CommonWithAuth";
import { subscriptionsState } from "@/stores/subscription";
import { smoothScrollToTarget } from "@/utils/scroll.util";
import { IconButton } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { Element } from "react-scroll";
import { useRecoilValue } from "recoil";
import styles from "./styles.module.less";

interface IProps {
  page: number;
  size: number;
}

const Subscription: NextPage<IProps> = ({ page, size }) => {
  const { loading, setField, field } = useSubscriptions({ page, size });
  const subscriptions = useRecoilValue(subscriptionsState);
  const router = useRouter();

  const { forward, backward } = useMemo(() => {
    const count = subscriptions.count;
    const currentMax = page * size;
    const cursor = { forward: true, backward: false };
    if (currentMax >= count) {
      cursor.forward = false;
    }
    if (page > 1) {
      cursor.backward = true;
    }
    return cursor;
  }, [subscriptions.count, page, size]);

  const handleForward = async () => {
    await smoothScrollToTarget("subscription-content");
    await router.push(`${router.pathname}?page=${page + 1}&size=${size}`);
  };

  const handleBackward = async () => {
    await smoothScrollToTarget("subscription-content");
    await router.push(`${router.pathname}?page=${page - 1}&size=${size}`);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (field?: GetSubsDtoField) => {
    if (field) {
      setField(field);
    }
    setAnchorEl(null);
  };

  const { t } = useTranslation("bundle");

  useMetaInfos(subscriptions?.results?.map((subscription) => subscription.id));

  return (
    <CommonLayoutWithAuth>
      <Head>
        <title>
          {t("menu:bundle.subscription")} - {t("menu:bundle.itself")} - MedicGo
        </title>
      </Head>
      <div className={styles.wrapperLeft}>
        <div>
          <Button
            onClick={() => router.push("/explore/navi")}
            color="primary"
            variant="outlined"
          >
            {t("sub.create")}
          </Button>
        </div>
        <div>
          <Button variant="outlined" color="default" onClick={handleClick}>
            {t("sub.order.itself")}
          </Button>
        </div>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => handleClose()}
        >
          <MenuItem
            onClick={() => handleClose(GetSubsDtoField.UPDATED_AT)}
            selected={field === GetSubsDtoField.UPDATED_AT}
          >
            {t("sub.order.update")}
          </MenuItem>
          <MenuItem
            onClick={() => handleClose(GetSubsDtoField.SUBSCRIBED_TIMES)}
            selected={field === GetSubsDtoField.SUBSCRIBED_TIMES}
          >
            {t("sub.order.times")}
          </MenuItem>
        </Menu>
        <div className={styles.wrapper}>
          {backward && (
            <IconButton onClick={handleBackward}>
              <ArrowBackIosIcon />
            </IconButton>
          )}
          {forward && (
            <IconButton onClick={handleForward}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </div>
      </div>
      <Element name="subscription-content">
        <div className={styles.grid}>
          {subscriptions.results?.map((sub) => (
            <SubscriptionCard sub={sub} key={sub.id} loading={loading} />
          ))}
        </div>
      </Element>
      <div className={styles.wrapper}>
        {backward && (
          <IconButton onClick={handleBackward}>
            <ArrowBackIosIcon />
          </IconButton>
        )}
        {forward && (
          <IconButton onClick={handleForward}>
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </div>
    </CommonLayoutWithAuth>
  );
};

Subscription.getInitialProps = async (ctx) => {
  const page = parseInt((ctx.query.page as string) || "1", 10);
  const size = parseInt((ctx.query.size as string) || "10", 10);
  return {
    page,
    size,
    namespacesRequired: ["common", "menu", "bundle"],
  };
};

export default Subscription;
